import { ChangeDetectionStrategy, Component, ComponentRef, effect, inject, Injector, OnDestroy, signal, untracked, viewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { logger } from '../utils/logger';
import { Visual } from './Visual';
import { VisualComponent } from './base/Visual.component';
import { VisualHostComponent } from './VisualHost.component';
import { VISUAL_HOST, VisualHost } from './VisualHost';

const LOGGER = logger('pbir-visual-shell');

const RECREATE_ON_CONTEXT_CHANGE = false;

@Component({
  selector: 'pbir-visual-shell',
  templateUrl: './VisualShell.component.html',
  styles: `
    pbir-visual-shell {
      display: block;
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
    }
    `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class VisualShellComponent implements OnDestroy {
  readonly host = inject(VisualHostComponent);

  readonly component = signal<VisualComponent>(null);

  private destroyed = false;

  private _visual: Visual;
  private _options: Visual.Options;

  private viewRef = viewChild('visual', { read: ViewContainerRef });

  private visualRef: ComponentRef<VisualComponent> = null;

  private readonly __busy_Changed_UpdateVisual = effect(() => {
    if (this.destroyed)
      return;

    let busy = this.host.busy();

    untracked(() => {
      let visualRef = this.visualRef;
      if (visualRef) {
        visualRef.setInput('busy', busy);
        visualRef.changeDetectorRef.detectChanges();
      }
    });
  });
  
  private readonly __dataSource_Changed_UpdateVisual = effect(() => {
    if (this.destroyed)
      return;

    let dataSource = this.host.dataSource();

    untracked(() => {
      let visualRef = this.visualRef;
      if (visualRef) {
        visualRef.setInput('dataSource', dataSource);
        visualRef.changeDetectorRef.detectChanges();
      }
    });
  });

  private readonly __visual_Changed_TryRecreateVisual = effect(() => {
    if (this.destroyed)
      return;

    try {
      let newVisual = this.host.visual();
      let oldVisual = this._visual;
      
      let newContext = newVisual.context;
      let newType = newVisual?.type;
      let newOptions = newVisual?.options();

      let viewRef = this.viewRef();

      untracked(() => {
        let visualRef = this.visualRef;
        let mustRecreate = true;
        let optionsChanged = true;

        if (oldVisual && newVisual) {
          let contextChanged = newContext !== oldVisual.context && RECREATE_ON_CONTEXT_CHANGE;
          let typeChanged = newType !== oldVisual.type;

          mustRecreate = !visualRef || contextChanged || typeChanged;
          
          let oldOptions = oldVisual?.options();
          optionsChanged = newOptions !== oldOptions;
          if (optionsChanged) {
            if (!newType.canReuse(this._options, newOptions))
              mustRecreate = true;
          }
        }

        if (!mustRecreate) {
          LOGGER.debug(`Visual component will be reused`, this);
          visualRef.setInput('visual', newVisual);
          visualRef.changeDetectorRef.detectChanges();
          return;
        }

        this.tryDestroyVisual();

        LOGGER.debug(
          `Creating visual component instance of the '${newType.name}' type`, this);

        let visualInjector = Injector.create({
          providers: [{
            provide: VISUAL_HOST,
            useValue: this.host
          }],
          parent: viewRef.injector
        });

        visualRef = viewRef.createComponent(newType.component, {
          injector: visualInjector
        });

        LOGGER.debug(
          `Visual component instance of the '${newType.name}' type successfully created`, this);

        // Assign reference, so if something goes wrong form here, we can destroy.
        this.visualRef = visualRef;

        visualRef.setInput('visual', newVisual);
        visualRef.setInput('busy', this.host.busy());
        visualRef.setInput('dataSource', this.host.dataSource());
        visualRef.changeDetectorRef.detectChanges();
        
        this._visual = newVisual;
        this._options = newOptions;

        this.component.set(visualRef.instance);
      });
    }
    catch (err) {
      try {
        LOGGER.error(`Unexpected error thrown when creating component ${this}`, this, err);

        this.tryDestroyVisual();
      }
      finally {
        this.host.broadcastEvent({
          type: VisualHost.EventType.RenderComplete,
          failed: true,
          error: err
        });
      }
    }
    finally {
      return null;
    }
  });

  private tryDestroyVisual() {
    untracked(() => {
      this._visual = null;
      this._options = null;

      const visualRef = this.visualRef;
      if (!visualRef)
        return;

      this.visualRef = null;

      try {
        this.component.set(null);
      }
      finally {
        try {
          visualRef.destroy();
        }
        catch (err) {
          LOGGER.error(
            `Unexpected error thrown when destroying ComponentRef<VisualComponent>`, this, err);
        }

        try {
          this.viewRef()?.clear();
        }
        catch (err) {
          LOGGER.error(
            `Unexpected error thrown when clearing the view container`, this, err);
        }
        finally {
        }
      }
    });
  }

  ngOnDestroy() {
    this.destroyed = true;

    this.tryDestroyVisual();
  }
}
